import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Star as StarIcon,
  TrendingUp as TrendingIcon,
  Home as PropertyIcon,
} from '@mui/icons-material';

interface ListingDescriptionTabProps {
  data: any;
  loading?: boolean;
  error?: string;
}

const ListingDescriptionTab: React.FC<ListingDescriptionTabProps> = ({
  data,
  loading = false,
  error,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography>Loading listing description analysis...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography color="text.secondary">No listing description data available</Typography>
      </Box>
    );
  }

  // Mock data structure - replace with actual API response structure
  const mockData = {
    listingDescription: {
      headline: "Stunning 3BR/2BA Home in Prime Location - Move-in Ready!",
      description: "Discover this beautifully maintained home featuring modern updates, spacious rooms, and a prime location. Perfect for families seeking comfort and convenience.",
      keyFeatures: [
        "Recently updated kitchen with granite countertops",
        "Hardwood floors throughout main level",
        "Large backyard perfect for entertaining",
        "New HVAC system (2023)",
        "Attached 2-car garage",
        "Excellent school district"
      ],
      highlights: [
        "Move-in ready condition",
        "Great curb appeal",
        "Convenient location near amenities",
        "Strong rental potential",
        "Low maintenance property"
      ],
      targetAudience: {
        primary: "Young families",
        secondary: "First-time homebuyers",
        investors: "Rental property investors"
      },
      marketingStrategy: {
        primaryChannels: ["Online listings", "Social media", "Real estate websites"],
        keywords: ["move-in ready", "family home", "updated", "prime location", "investment property"],
        seasonalTiming: "Spring/Summer optimal"
      }
    },
    confidence: 85,
    dataQuality: "high"
  };

  const listingData = data.listingDescription || mockData.listingDescription;

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DescriptionIcon color="primary" />
        Listing Description Generator
      </Typography>

      {/* Headline Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Property Headline
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            {listingData.headline}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {listingData.description}
          </Typography>
        </CardContent>
      </Card>

      {/* Key Features */}
      <Box sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon color="primary" />
              Key Features
            </Typography>
            <Box>
              {listingData.keyFeatures?.map((feature: string, index: number) => (
                <Box key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                    }}
                  />
                  <Typography variant="body2">{feature}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Property Highlights */}
      <Box sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingIcon color="success" />
              Property Highlights
            </Typography>
            <Box>
              {listingData.highlights?.map((highlight: string, index: number) => (
                <Chip
                  key={index}
                  label={highlight}
                  variant="outlined"
                  color="success"
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Target Audience & Marketing Strategy */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PropertyIcon color="info" />
                Target Audience
              </Typography>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Primary: <Chip label={listingData.targetAudience?.primary} size="small" color="primary" />
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Secondary: <Chip label={listingData.targetAudience?.secondary} size="small" color="secondary" />
                </Typography>
                <Typography variant="subtitle2">
                  Investors: <Chip label={listingData.targetAudience?.investors} size="small" color="info" />
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingIcon color="warning" />
                Marketing Strategy
              </Typography>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Primary Channels:
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {listingData.marketingStrategy?.primaryChannels?.map((channel: string, index: number) => (
                    <Chip
                      key={index}
                      label={channel}
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Keywords:
                </Typography>
                <Box>
                  {listingData.marketingStrategy?.keywords?.map((keyword: string, index: number) => (
                    <Chip
                      key={index}
                      label={keyword}
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Seasonal Timing */}
      <Box sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingIcon color="secondary" />
              Optimal Listing Timing
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Best Time to List:</strong> {listingData.marketingStrategy?.seasonalTiming}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This timing recommendation is based on market trends, seasonal buyer behavior, and local market conditions.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Data Quality Indicator */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Chip
          label={`${data.confidence || mockData.confidence}% Confidence`}
          color="success"
          variant="outlined"
          sx={{ mr: 1 }}
        />
        <Chip
          label={`${data.dataQuality || mockData.dataQuality} Quality Data`}
          color="info"
          variant="outlined"
        />
      </Box>
    </Box>
  );
};

export default ListingDescriptionTab;
